import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Building2, Search, Upload, Download, Printer, Trash2, Pencil, FileText,
  Image as ImageIcon, FileType, History, ShieldOff, ArrowLeft, ChevronRight,
  CheckSquare, Square, RefreshCw, FolderOpen, FileCheck2, AlertCircle,
  Package, Info,
} from "lucide-react";

const iconForExt = (ext) => {
  const e = (ext || "").toLowerCase();
  if (e === "pdf") return <FileText className="w-4 h-4 text-red-500" />;
  if (["jpg", "jpeg", "png"].includes(e)) return <ImageIcon className="w-4 h-4 text-blue-500" />;
  if (e === "docx") return <FileType className="w-4 h-4 text-indigo-500" />;
  return <FileText className="w-4 h-4 text-slate-500" />;
};

const humanSize = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

/* ============================================================
   Root component
============================================================ */
const CompanyPrintPanel = ({ user }) => {
  const isAdmin = user?.role === "admin";
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [meta, setMeta] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        axios.get(`${API}/company-print/companies`, { headers: getAuthHeader() }),
        axios.get(`${API}/company-print/meta`, { headers: getAuthHeader() }),
      ]);
      setCompanies(c.data.companies || []);
      setMeta(m.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load companies");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return companies;
    const rx = q.trim().toLowerCase();
    return companies.filter((c) =>
      (c.name || "").toLowerCase().includes(rx) ||
      (c.gstin || "").toLowerCase().includes(rx) ||
      (c.iec || "").toLowerCase().includes(rx)
    );
  }, [companies, q]);

  if (selectedCompany) {
    return (
      <CompanyDocumentsView
        company={selectedCompany}
        meta={meta}
        isAdmin={isAdmin}
        onBack={() => { setSelectedCompany(null); load(); }}
        currentUser={user}
      />
    );
  }

  return (
    <div className="min-h-full bg-slate-50 p-4 md:p-6 space-y-5" data-testid="company-print-panel">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-2" data-testid="company-print-heading">
            <Building2 className="w-8 h-8 text-indigo-600" />
            Company Wise Print
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Central document library per company — upload once, reprint any document (or the entire set) whenever an export shipment needs it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-white text-xs">
            <FileCheck2 className="w-3 h-3 mr-1 text-emerald-600" />
            {isAdmin ? "Administrator" : "Processing Team"}
          </Badge>
          <Button size="sm" variant="outline" onClick={load} data-testid="company-print-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search company name, GSTIN, IEC…"
              className="pl-9"
              data-testid="company-search-input"
            />
          </div>

          {loading ? (
            <div className="text-center text-slate-400 py-8 text-sm">Loading companies…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-slate-400 py-8 text-sm italic">
              {q ? "No matching companies." : "No companies yet — add one via Documents → Manage Companies."}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="company-grid">
              {filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedCompany(c)}
                  className={`text-left p-4 rounded-lg border-2 bg-white hover:border-indigo-400 hover:shadow-md transition-all ${
                    c.is_active === false ? "opacity-60 border-slate-200" : "border-slate-200"
                  }`}
                  data-testid={`company-card-${c.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">
                        <Building2 className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-800 text-sm leading-tight">{c.name}</div>
                        <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                          {c.gstin || "—"} · {c.iec || "—"}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1 text-slate-700">
                      <Package className="w-3 h-3" />
                      <b>{c.document_count || 0}</b> docs
                    </span>
                    {c.last_updated_at && (
                      <span className="text-slate-400">
                        {new Date(c.last_updated_at).toLocaleDateString()}
                      </span>
                    )}
                    {c.is_active === false && (
                      <Badge variant="outline" className="text-[9px] text-amber-700 border-amber-300">Inactive</Badge>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* ============================================================
   Per-company documents view
============================================================ */
const CompanyDocumentsView = ({ company, meta, isAdmin, onBack, currentUser }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [fileTypeFilter, setFileTypeFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [conflict, setConflict] = useState(null); // {existing, pendingFormData, pendingMeta}
  const [auditOpen, setAuditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (categoryFilter !== "all") params.set("category", categoryFilter);
      if (fileTypeFilter !== "all") params.set("file_type", fileTypeFilter);
      const res = await axios.get(
        `${API}/company-print/companies/${company.id}/documents?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      setDocs(res.data.documents || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load documents");
    } finally { setLoading(false); }
  }, [company.id, q, categoryFilter, fileTypeFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(docs.map((d) => d.id)));
  const clearSelection = () => setSelected(new Set());

  const printOne = async (doc) => {
    try {
      const res = await axios.get(
        `${API}/company-print/documents/${doc.id}/download`,
        { headers: getAuthHeader(), responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank");
      if (!w) { toast.error("Please allow popups to print"); return; }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
  };

  const printSet = async (ids = null) => {
    try {
      const res = await axios.post(
        `${API}/company-print/companies/${company.id}/print-set`,
        { document_ids: ids },
        { headers: getAuthHeader(), responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank");
      if (!w) { toast.error("Please allow popups to print"); return; }
      toast.success(ids ? `${ids.length} document(s) merged` : "Company set merged");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      // Blob may hide the JSON detail — extract it
      let msg = "Print failed";
      try {
        const text = await e?.response?.data?.text?.();
        if (text) { const j = JSON.parse(text); msg = j.detail || msg; }
      } catch { /* ignore */ }
      toast.error(msg);
    }
  };

  const delDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.document_name}" v${doc.version}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/company-print/documents/${doc.id}`, { headers: getAuthHeader() });
      toast.success("Document deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // ---- upload flow with conflict prompt ----
  const submitUpload = async (payload) => {
    // payload = {file, document_name, category, description, on_conflict, replace_document_id}
    const fd = new FormData();
    fd.append("file", payload.file);
    fd.append("document_name", payload.document_name);
    fd.append("category", payload.category || "");
    fd.append("description", payload.description || "");
    fd.append("on_conflict", payload.on_conflict || "prompt");
    if (payload.replace_document_id) fd.append("replace_document_id", payload.replace_document_id);
    try {
      await axios.post(
        `${API}/company-print/companies/${company.id}/documents`,
        fd,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } }
      );
      toast.success("Uploaded");
      setUploadOpen(false); setConflict(null); load();
    } catch (err) {
      if (err?.response?.status === 409 && err?.response?.data?.action_required === "conflict") {
        setConflict({
          existing: err.response.data.existing,
          message: err.response.data.message,
          payload,
        });
        return;
      }
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  return (
    <div className="min-h-full bg-slate-50 p-4 md:p-6 space-y-4" data-testid="company-documents-view">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="back-to-companies">
            <ArrowLeft className="w-4 h-4 mr-1" /> All Companies
          </Button>
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" /> {company.name}
            </h2>
            <div className="text-xs text-slate-500 font-mono">
              GSTIN {company.gstin || "—"} · IEC {company.iec || "—"}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => printSet(null)} disabled={docs.length === 0}
                  data-testid="print-full-set">
            <Printer className="w-4 h-4 mr-1" /> Print Full Company Set
          </Button>
          <Button size="sm" variant="outline" onClick={() => printSet(Array.from(selected))}
                  disabled={selected.size === 0} data-testid="print-selected">
            <Printer className="w-4 h-4 mr-1" /> Print Selected ({selected.size})
          </Button>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => setUploadOpen(true)}
                  disabled={company.is_active === false}
                  data-testid="upload-new-doc">
            <Upload className="w-4 h-4 mr-1" /> Upload Document
          </Button>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setAuditOpen(true)}
                    data-testid="view-audit-trail">
              <History className="w-4 h-4 mr-1" /> Audit
            </Button>
          )}
        </div>
      </div>

      {company.is_active === false && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> This company is deactivated. Existing documents can still be printed, but no new uploads are allowed.
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
            <div className="md:col-span-6 relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search document name / description / file…"
                     className="pl-9" data-testid="docs-search-input" />
            </div>
            <div className="md:col-span-3">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger data-testid="docs-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {(meta?.categories || []).map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Select value={fileTypeFilter} onValueChange={setFileTypeFilter}>
                <SelectTrigger data-testid="docs-file-type-filter"><SelectValue placeholder="All file types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All File Types</SelectItem>
                  {(meta?.allowed_extensions || []).map((e) => (<SelectItem key={e} value={e}>{e.toUpperCase()}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents table */}
      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="text-center text-slate-400 py-8 text-sm">Loading documents…</div>
          ) : docs.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-2" />
              <div className="text-sm text-slate-500 italic">No documents yet — click Upload Document.</div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-2 text-xs text-slate-600">
                <button onClick={selected.size === docs.length ? clearSelection : selectAll}
                        className="inline-flex items-center gap-1 hover:text-indigo-600" data-testid="toggle-select-all">
                  {selected.size === docs.length ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                  {selected.size === docs.length ? "Clear selection" : "Select all"}
                </button>
                <span>·</span>
                <span>{docs.length} document{docs.length !== 1 ? "s" : ""}</span>
                <span className="text-slate-400 flex items-center gap-1">
                  <Info className="w-3 h-3" /> DOCX files can be downloaded individually but are skipped from merged prints.
                </span>
              </div>
              <div className="border rounded-md overflow-x-auto bg-white">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Document</TableHead>
                      <TableHead className="w-32">Category</TableHead>
                      <TableHead className="w-16">Type</TableHead>
                      <TableHead className="w-14 text-right">Ver</TableHead>
                      <TableHead className="w-24 text-right">Size</TableHead>
                      <TableHead className="w-40">Uploaded</TableHead>
                      <TableHead className="w-44 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {docs.map((d) => (
                      <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                        <TableCell>
                          <Checkbox checked={selected.has(d.id)}
                                    onCheckedChange={() => toggleSelect(d.id)}
                                    data-testid={`doc-select-${d.id}`} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {iconForExt(d.ext)}
                            <div>
                              <div className="font-medium text-sm">{d.document_name}</div>
                              <div className="text-[11px] text-slate-500 truncate max-w-[280px]">
                                {d.original_filename}{d.description ? ` · ${d.description}` : ""}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">
                          {d.category ? <Badge variant="outline" className="text-[10px]">{d.category}</Badge> : "—"}
                        </TableCell>
                        <TableCell className="text-xs uppercase text-slate-600">{d.ext}</TableCell>
                        <TableCell className="text-xs text-right font-mono">v{d.version}</TableCell>
                        <TableCell className="text-xs text-right text-slate-600">{humanSize(d.size)}</TableCell>
                        <TableCell className="text-xs text-slate-600">
                          <div>{new Date(d.uploaded_at).toLocaleDateString()}</div>
                          <div className="text-[11px] text-slate-400">{d.uploaded_by}</div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7"
                                    onClick={() => printOne(d)} title="Print / View" data-testid={`doc-print-${d.id}`}>
                              <Printer className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7"
                                    onClick={() => printOne(d)} title="Download" data-testid={`doc-download-${d.id}`}>
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                            {isAdmin && (
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500"
                                      onClick={() => delDoc(d)} title="Delete" data-testid={`doc-delete-${d.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Upload dialog */}
      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        meta={meta}
        onSubmit={submitUpload}
      />

      {/* Conflict prompt dialog */}
      <ConflictDialog
        conflict={conflict}
        onCancel={() => setConflict(null)}
        onNewVersion={() => submitUpload({ ...conflict.payload, on_conflict: "new_version" })}
        onReplace={() => submitUpload({
          ...conflict.payload,
          on_conflict: "replace",
          replace_document_id: conflict.existing.id,
        })}
      />

      {/* Audit trail dialog */}
      {isAdmin && (
        <AuditDialog open={auditOpen} onClose={() => setAuditOpen(false)} companyId={company.id} />
      )}
    </div>
  );
};

/* ============================================================
   Upload dialog
============================================================ */
const UploadDialog = ({ open, onClose, meta, onSubmit }) => {
  const [file, setFile] = useState(null);
  const [documentName, setDocumentName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (!open) {
      setFile(null); setDocumentName(""); setCategory(""); setDescription("");
      setSaving(false); if (fileRef.current) fileRef.current.value = "";
    }
  }, [open]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    if (!documentName) {
      const base = f.name.replace(/\.[^.]+$/, "");
      setDocumentName(base);
    }
  };

  const submit = async () => {
    if (!file) { toast.error("Please choose a file"); return; }
    if (!documentName.trim()) { toast.error("Please enter a document name"); return; }
    setSaving(true);
    await onSubmit({ file, document_name: documentName.trim(), category, description, on_conflict: "prompt" });
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="upload-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-4 h-4" /> Upload Document
          </DialogTitle>
          <DialogDescription>
            Supported: {(meta?.allowed_extensions || []).join(", ").toUpperCase()} · Max {meta?.max_size_mb || 25} MB.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">File</Label>
            <Input type="file" ref={fileRef} onChange={onFileChange}
                   accept=".pdf,.jpg,.jpeg,.png,.docx"
                   className="mt-1" data-testid="upload-file-input" />
            {file && (
              <div className="text-xs text-slate-500 mt-1">
                {file.name} · {humanSize(file.size)}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Document Name</Label>
            <Input value={documentName} onChange={(e) => setDocumentName(e.target.value)}
                   placeholder="e.g. GST Certificate"
                   className="mt-1" data-testid="upload-name-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category (optional)</Label>
              <Select value={category || "__none__"} onValueChange={(v) => setCategory(v === "__none__" ? "" : v)}>
                <SelectTrigger className="mt-1" data-testid="upload-category-select">
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {(meta?.categories || []).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Description / Remarks</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)}
                     placeholder="e.g. Renewed on 12-Jul-2026"
                     className="mt-1" data-testid="upload-description-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="upload-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="upload-submit">
            {saving ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Uploading…</> : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ============================================================
   Conflict prompt dialog
============================================================ */
const ConflictDialog = ({ conflict, onCancel, onNewVersion, onReplace }) => {
  if (!conflict) return null;
  return (
    <Dialog open={!!conflict} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent data-testid="conflict-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-amber-800 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> Document already exists
          </DialogTitle>
          <DialogDescription>{conflict.message}</DialogDescription>
        </DialogHeader>
        <div className="text-xs bg-slate-50 border rounded p-3 space-y-1">
          <div><b>Existing:</b> {conflict.existing.document_name}</div>
          <div><b>Version:</b> v{conflict.existing.version}</div>
          <div><b>File:</b> {conflict.existing.original_filename}</div>
          <div><b>Uploaded:</b> {new Date(conflict.existing.uploaded_at).toLocaleString()} by {conflict.existing.uploaded_by}</div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="conflict-cancel">Cancel</Button>
          <Button variant="outline" onClick={onReplace} className="text-red-600 border-red-300"
                  data-testid="conflict-replace">
            <RefreshCw className="w-4 h-4 mr-1" /> Replace (soft-delete old)
          </Button>
          <Button onClick={onNewVersion} className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="conflict-new-version">
            Add as new version
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ============================================================
   Audit dialog (admin only)
============================================================ */
const AuditDialog = ({ open, onClose, companyId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/company-print/companies/${companyId}/audit?limit=200`, { headers: getAuthHeader() })
      .then((r) => setRows(r.data.audit || []))
      .catch(() => toast.error("Failed to load audit"))
      .finally(() => setLoading(false));
  }, [open, companyId]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="audit-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4" /> Audit Trail
          </DialogTitle>
          <DialogDescription>Last 200 actions on this company&apos;s document library.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-center py-8 text-sm text-slate-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8 text-sm italic text-slate-400">No audit entries.</div>
        ) : (
          <div className="max-h-[420px] overflow-y-auto border rounded">
            <Table>
              <TableHeader className="bg-slate-50 sticky top-0">
                <TableRow>
                  <TableHead className="text-xs">When</TableHead>
                  <TableHead className="text-xs">Action</TableHead>
                  <TableHead className="text-xs">Document</TableHead>
                  <TableHead className="text-xs">User</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i} data-testid={`audit-row-${i}`}>
                    <TableCell className="text-xs whitespace-nowrap">{new Date(r.at).toLocaleString()}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px] capitalize">{r.action}</Badge></TableCell>
                    <TableCell className="text-xs">
                      {r.extra?.document_name || "—"}
                      {r.extra?.version && <span className="text-slate-400"> (v{r.extra.version})</span>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{r.user_name || r.user_email || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompanyPrintPanel;
